/* eslint-disable @typescript-eslint/no-explicit-any */
import { AddressPipe } from '@amfam/policy/pipes/lib/pipes/policy.pipes';

import { Address } from './address';
import { Coverage } from './coverage';
import { NamedInsured } from './named-insured';
import { Policy } from './policy';
import { PolicyTypeIconConstants } from './policy-type-icon-constants';
import { SourceSystem } from './source-system-type';

export class PropertyPolicy extends Policy {
  public riskLocationAddress?: Address;
  public propertyCoverages: Coverage[] = new Array<Coverage>();
  public dwellingRoles: NamedInsured[] = new Array<NamedInsured>();
  eoiLink?: string;

  private removeSubLimits(coverage: any, coveragesToFilter: any) {
    if (!coveragesToFilter || coveragesToFilter.length === 0) return coverage;

    // Removing the sublimits from the coverage based on the filters in the policy-copy.json
    for (const coverageToFilter of coveragesToFilter) {
      const { coverageCode, limitTerm, packageTerm } = coverageToFilter;
      if (coverage.code === coverageCode) {
        return {
          ...coverage,
          limitTerms: limitTerm
            ? [
                ...coverage.limitTerms
                  .filter((lt: any) => lt.code === limitTerm)
                  .map((l: any) => ({
                    ...l,
                    packageTerms: packageTerm
                      ? [...l.packageTerms.filter((pt: any) => pt.termType === packageTerm)]
                      : [...l.packageTerms]
                  }))
              ]
            : [...coverage.limitTerms]
        };
      }
    }

    return coverage;
  }

  private replaceCoverage(coverage: any, coveragesToReplace: any) {
    if (!coveragesToReplace || coveragesToReplace.length === 0) return coverage;

    // Replace the coverage text from the coverage based on the config in  policy-copy.json
    for (const coverageToReplace of coveragesToReplace) {
      const { coverageR, limitR, packageR } = coverageToReplace;
      const coverageCode = coverageR.code;
      const limitTerm = limitR.code;
      const packageTerm = packageR.code;
      const coverageCodeToReplace = coverageR.replace;
      const limitTermToReplace = limitR.replace;
      const packageTermToReplace = packageR.replace;

      if (coverage.code === coverageCode) {
        return {
          ...coverage,
          description: coverageCodeToReplace
            ? coverage?.description?.replace(coverageCodeToReplace.from, coverageCodeToReplace.to)
            : coverage?.description,
          limitTerms: this.replaceLimitTerm(
            coverage,
            limitTerm,
            limitTermToReplace,
            packageTerm,
            packageTermToReplace
          )
        };
      }
    }

    return coverage;
  }

  constructor(json: any, coveragesToFilter?: any, coveragesToReplace?: any) {
    super(json);
    this.iconType = PolicyTypeIconConstants.HOME;
    this.policyType = 'property';

    if (json.dwelling.riskLocationAddress) {
      this.riskLocationAddress = new Address(json.dwelling.riskLocationAddress);
      this.policyRisks.push(this.riskLocationAddress);
    }

    if (json.dwelling.dwellingRoles) {
      for (const insured of json.dwelling.dwellingRoles) {
        this.dwellingRoles.push(new NamedInsured(insured));
      }
    }

    if (json.dwelling.dwellingCoverages) {
      for (const coverage of json.dwelling.dwellingCoverages) {
        const coverageEntity = new Coverage(
          this.replaceCoverage(
            this.removeSubLimits(coverage, coveragesToFilter),
            coveragesToReplace
          ),
          this.sourcePath !== SourceSystem.ROME
        );
        if (Coverage.isValid(coverageEntity)) {
          this.propertyCoverages.push(coverageEntity);
        }
      }
    }

    this.riskDescriptionList = [new AddressPipe().transform(this.policyAddress, 'short')];
  }

  private replacePackageTerm(
    l?: any,
    limitTerm?: any,
    packageTerm?: any,
    packageTermToReplace?: any
  ) {
    return packageTermToReplace
      ? [
          ...l.packageTerms.map((pt: any) =>
            packageTerm === pt.termType && l.code === limitTerm
              ? {
                  ...pt,
                  description: pt.description?.replace(
                    packageTermToReplace.from,
                    packageTermToReplace.to
                  )
                }
              : pt
          )
        ]
      : [...l.packageTerms];
  }

  private replaceLimitTerm(
    coverage?: any,
    limitTerm?: any,
    limitTermToReplace?: any,
    packageTerm?: any,
    packageTermToReplace?: any
  ) {
    return limitTermToReplace
      ? [
          ...coverage.limitTerms
            .map((lt: any) =>
              limitTerm === lt.code
                ? {
                    ...lt,
                    description: lt.description?.replace(
                      limitTermToReplace.from,
                      limitTermToReplace.to
                    )
                  }
                : lt
            )
            .map((l: any) => ({
              ...l,
              packageTerms: this.replacePackageTerm(l, limitTerm, packageTerm, packageTermToReplace)
            }))
        ]
      : [
          ...coverage.limitTerms.map((l: any) => ({
            ...l,
            packageTerms: this.replacePackageTerm(l, limitTerm, packageTerm, packageTermToReplace)
          }))
        ];
  }
}
